import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography
} from '@mui/material';
import BalanceOutlinedIcon from '@mui/icons-material/BalanceOutlined';

import { useOpenerDescalce } from '../api/useOpenerDescalce';
import { useTriageIndex } from '../api/useTriageIndex';
import { useOpenerDescalceSocket } from '../hooks/useOpenerDescalceSocket';
import type { OpenerDescalceRow } from '../api/useOpenerDescalce';
import {
  DEFAULT_DESCALCE_FILTERS,
  applyDescalceFilters,
  computeDescalceTotals,
  countGroupsOnHidden,
  dropResolvedRows,
  pruneAccountFilter,
  type DescalceFilterState
} from '../components/descalce/descalceFilters';
import { legCount } from '../components/descalce/descalceLegs';
import {
  DEFAULT_DESCALCE_SORT,
  sortDescalceRows,
  type DescalceSortColumn,
  type DescalceSortState
} from '../components/descalce/descalceSort';
import {
  useDescalceFreshnessFrame,
  useDescalceStale
} from '../components/descalce/descalceFreshness';
import { resolveDescalceViewState } from '../components/descalce/descalceViewState';
import { DescalceHeaderStrip } from '../components/descalce/DescalceHeaderStrip';
import { DescalceFilterBar } from '../components/descalce/DescalceFilterBar';
import { DescalceEmptyState } from '../components/descalce/DescalceEmptyState';
import {
  BeliefCell,
  DescalceCell,
  DiffCell,
  GoCell,
  GroupCell,
  RuleCell,
  SideCell,
  TriageCell,
  VenueCountCell
} from '../components/descalce/DescalceCells';
import { resolveTriageRowCounts } from '../components/triage/triageRowCounts';
import {
  hasTriageMarks,
  triageMarkTotal
} from '../components/triage/triageMarkText';

interface ColumnDef {
  key: string;
  label: string;
  sort?: DescalceSortColumn;
  align?: 'left' | 'right' | 'center';
  /** Header `title` when the label itself is a glyph. Defaults to `label`. */
  title?: string;
  /**
   * Relative track weight, emitted as a PERCENTAGE of `COL_TOTAL_PX` — the
   * idiom of the other fixed tables in this module (`UnwindingPage`'s
   * `TNA_COL_WIDTHS_PX` / `TNA_COL_TOTAL_PX`, `EntryTabByPath`). Every column
   * MUST carry one: under `table-layout: fixed` a width-less column is handed
   * only what is left over, which reaches `0` on a narrow viewport.
   */
  widthPx: number;
}

/**
 * `market` carries by far the largest weight on purpose: the pair name is the
 * only unbounded text on the screen, while the numeric columns never hold more
 * than a few digits.
 */
const COLUMNS: ColumnDef[] = [
  { key: 'descalce', label: 'Descalce', sort: 'descalce', align: 'right', widthPx: 82 },
  { key: 'side', label: 'Side', widthPx: 76 },
  { key: 'group', label: 'Group', sort: 'group', widthPx: 250 },
  { key: 'market', label: 'Market', sort: 'pairName', widthPx: 700 },
  { key: 'polyYes', label: 'Poly Y', sort: 'polyYes', align: 'right', widthPx: 62 },
  { key: 'polyNo', label: 'Poly N', sort: 'polyNo', align: 'right', widthPx: 62 },
  { key: 'kalshiYes', label: 'Kalshi Y', sort: 'kalshiYes', align: 'right', widthPx: 72 },
  { key: 'kalshiNo', label: 'Kalshi N', sort: 'kalshiNo', align: 'right', widthPx: 72 },
  { key: 'belief', label: 'Belief', sort: 'belief', align: 'right', widthPx: 64 },
  { key: 'diff', label: 'Diff', sort: 'divergence', align: 'right', widthPx: 68 },
  { key: 'rule', label: 'Rule', sort: 'ruleStatus', widthPx: 88 },
  {
    key: 'triage',
    label: '⚠',
    title: 'Unread stuck-counter / orphaned-fill cards',
    sort: 'triage',
    align: 'center',
    widthPx: 46
  },
  { key: 'go', label: 'Go', align: 'center', widthPx: 48 }
];

const COL_TOTAL_PX = COLUMNS.reduce((sum, col) => sum + col.widthPx, 0);

/** Narrow tracks can't afford MUI's default 16px right padding on top of a
 *  sort label's always-rendered icon, so every column but `market` trims it. */
const tightCellSx = { px: 0.75 } as const;

/**
 * Compact header, `hSx` in `UnwindingPage` being the precedent. MUI's
 * `TableSortLabel` always reserves ~22px for the sort icon even when
 * inactive, so a label is budgeted at `track - 22 - padding`; `nowrap` +
 * `ellipsis` keeps one that outgrows that budget inside its own cell instead
 * of bleeding across the border, and every header carries a `title` so the
 * full word stays reachable.
 */
const headerCellSx = {
  fontSize: '0.58rem',
  lineHeight: 1.2,
  whiteSpace: 'nowrap' as const,
  overflow: 'hidden',
  textOverflow: 'ellipsis' as const
} as const;

const fmtTime = (ms: number): string => new Date(ms).toLocaleTimeString();

export const DescalcePage = () => {
  // Push first: every sweep the sweeper publishes lands here over the
  // `/descalce` socket and is written into the query cache below. The query
  // still owns the data — the socket only fills it faster — and its REST poll
  // backs off to a safety net for as long as frames keep arriving.
  const { pushActive } = useOpenerDescalceSocket();
  const { data, isLoading, isError, error } = useOpenerDescalce({ pushActive });

  // The marks come from their OWN source (spec §6.1) and share nothing with
  // the sweeper snapshot: an unreadable index must not blank this screen, and
  // an unreadable snapshot must not be reported as "no triage".
  const triage = useTriageIndex();

  const [filters, setFilters] = useState<DescalceFilterState>(
    DEFAULT_DESCALCE_FILTERS
  );
  const [sort, setSort] = useState<DescalceSortState>(DEFAULT_DESCALCE_SORT);

  // An account the snapshot stops carrying loses its toggle, but its key would
  // keep filtering — an un-clickable selection that hides every row with
  // nothing on screen to explain it. Dropping the key un-scopes instead.
  useEffect(() => {
    setFilters(prev => {
      const pruned = pruneAccountFilter(prev.accounts, data?.accounts);
      return pruned.length === prev.accounts.length
        ? prev
        : { ...prev, accounts: pruned };
    });
  }, [data?.accounts]);

  const triageCountsOf = useCallback(
    (row: OpenerDescalceRow) => resolveTriageRowCounts(triage.data, row),
    [triage.data]
  );

  // One object for the filter and the comparator: both ask the same question
  // of the same index, and two lookups would be two chances to disagree.
  const triageLookup = useMemo(
    () => ({
      hasTriage: (row: OpenerDescalceRow) =>
        hasTriageMarks(triageCountsOf(row)),
      triageCount: (row: OpenerDescalceRow) =>
        triageMarkTotal(triageCountsOf(row))
    }),
    [triageCountsOf]
  );

  const frame = useDescalceFreshnessFrame(data?.computedAt);
  const stale = useDescalceStale(frame, data?.staleMs);

  const viewState = resolveDescalceViewState({
    isLoading,
    isError,
    error,
    data
  });

  // A resolved leg never reaches the table. The sweeper already leaves those
  // rows out (and counts them into `resolvedExcluded`); this drop is what
  // holds the rule during a deploy window, when the wire can still carry them.
  const { live, resolved } = useMemo(
    () => dropResolvedRows(data?.rows ?? []),
    [data]
  );

  const visibleRows: OpenerDescalceRow[] = useMemo(
    () =>
      sortDescalceRows(
        applyDescalceFilters(live, filters, triageLookup),
        sort,
        triageLookup
      ),
    [live, filters, sort, triageLookup]
  );

  const totals = useMemo(
    () => computeDescalceTotals(visibleRows, triageLookup),
    [visibleRows, triageLookup]
  );

  // The sweeper's own count of what it left out, plus whatever this page
  // dropped itself. `resolvedExcluded` is an UNVALIDATED cast off the wire:
  // `'7' + 1` is `'71'`, and this chip is the one line that exists to keep the
  // exclusion honest.
  //
  // 🚨 ABSENT and PRESENT-BUT-MALFORMED are different facts. Absent is a
  // backend older than the count, and its rows still arrive for
  // `dropResolvedRows` to catch. Malformed is a count we cannot read over rows
  // we never got — and treating it as `0` would put the screen back exactly
  // where it started: zero rows, no chip, and an `empty` state announcing "no
  // open positions on any opener rule right now" over N excluded rules.
  const rawExcluded = data?.resolvedExcluded;
  const excludedUsable =
    typeof rawExcluded === 'number' &&
    Number.isInteger(rawExcluded) &&
    rawExcluded >= 0;
  // `!== undefined`, NOT `!= null`: `null` is a value the field CARRIES, and
  // the rule above is about presence, not about truthiness. Folding an
  // explicit `null` into "absent" would hand it the absent case's assumption
  // — that the rows came anyway, for `dropResolvedRows` to catch — which is
  // the one thing a present-but-unreadable count cannot promise.
  const excludedUnreadable = rawExcluded !== undefined && !excludedUsable;
  const backendExcluded = excludedUsable ? rawExcluded : 0;
  const resolvedExcluded = backendExcluded + resolved.length;
  /** What the DEFAULT-ON `groups ON` filter alone is suppressing. */
  const groupsOnHidden = useMemo(
    () => countGroupsOnHidden(live, filters, triageLookup),
    [live, filters, triageLookup]
  );

  // 🚨 The state machine reads the SNAPSHOT, and the sweeper no longer puts a
  // resolved row in it — so "every rule holds a resolved leg" arrives as zero
  // rows and would render `empty`: "No open positions on any opener rule right
  // now", three centimetres from a chip saying N were excluded. The page has
  // its own message for that; this is what lets it be reached on the ordinary
  // path, not only when a pre-change backend still sends the rows.
  const everyRowExcluded =
    viewState === 'empty' && (resolvedExcluded > 0 || excludedUnreadable);

  /**
   * Rules the sweeper REFUSED because it could not resolve their venue-account
   * pair. They hold positions — the rule universe is derived from held markets
   * — and they are not in `rows`, so an empty table is not "nothing is open".
   *
   * 🚨 Validated because it is a cast off the wire, and because these two
   * counts are the ONLY things standing between an empty table and a screen
   * announcing that nothing is open. A count that degrades to `0` on a
   * malformed value brings that announcement back through the neighbour.
   *
   * ⚠️ It does NOT treat "absent" the way `resolvedExcluded` does, and the
   * difference is deliberate. There, absence means an older backend — one that
   * still SENDS the rows, so the page can count them itself. Here there is
   * nothing to count from: a refused rule has no row anywhere by definition,
   * so a missing count is as blind as a malformed one. Both are unreadable.
   */
  const rawUnresolved = data?.unresolvedAccounts?.ruleCount;
  const unresolvedUsable =
    typeof rawUnresolved === 'number' &&
    Number.isInteger(rawUnresolved) &&
    rawUnresolved >= 0;
  const unresolvedUnreadable = data !== undefined && !unresolvedUsable;
  const unresolvedRuleCount = unresolvedUsable ? rawUnresolved : 0;
  const onlyUnresolvedLeft =
    viewState === 'empty' &&
    !everyRowExcluded &&
    (unresolvedRuleCount > 0 || unresolvedUnreadable);
  /**
   * Appended to the messages that explain WHY a body is empty with a cause no
   * filter reverses. `filtered-empty` deliberately does not carry it: that one
   * makes no claim about the fleet, it says the filters are hiding rows.
   */
  const unresolvedSuffix = unresolvedUnreadable
    ? ' The number of rules refused for an unresolvable account pair could not be read — it may be none.'
    : unresolvedRuleCount > 0
      ? ` ${unresolvedRuleCount} rule(s) hold a position and were refused for an unresolvable account pair — see the chip above.`
      : '';

  const toggleSort = (column: DescalceSortColumn) => {
    setSort(prev =>
      prev.column === column
        ? { column, direction: prev.direction === 'desc' ? 'asc' : 'desc' }
        : { column, direction: 'desc' }
    );
  };

  const lastSuccessLabel = frame ? fmtTime(frame.computedAt) : null;

  return (
    <Box
      sx={{
        px: 2,
        pt: 1,
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{ mb: 1, flexShrink: 0 }}
      >
        <BalanceOutlinedIcon sx={{ color: 'text.secondary' }} />
        <Typography variant="h5" sx={{ color: 'common.white' }}>
          Descalces
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', color: 'grey.500' }}>
          Cross-group opener leg imbalance — read-only triage, largest naked leg
          first.
        </Typography>
      </Stack>

      {stale && (
        <Alert severity="warning" variant="outlined" sx={{ mb: 1, flexShrink: 0 }}>
          Stale — last read {lastSuccessLabel ?? 'unknown'}. Showing the last
          known snapshot; this does NOT substitute another data source.
        </Alert>
      )}

      <DescalceFilterBar
        filters={filters}
        onChange={setFilters}
        accounts={data?.accounts}
      />
      <DescalceHeaderStrip
        totals={totals}
        snapshot={data}
        resolvedExcluded={resolvedExcluded}
        groupsOnHidden={groupsOnHidden}
        unresolvedRuleCount={unresolvedRuleCount}
        unresolvedUnreadable={unresolvedUnreadable}
        excludedUnreadable={excludedUnreadable}
        triage={triage}
      />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <TableContainer>
          <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
            <colgroup>
              {COLUMNS.map(col => (
                <col
                  key={col.key}
                  style={{ width: `${(col.widthPx / COL_TOTAL_PX) * 100}%` }}
                />
              ))}
            </colgroup>
            <TableHead>
              <TableRow>
                {COLUMNS.map(col => (
                  <TableCell
                    key={col.key}
                    align={col.align ?? 'left'}
                    title={col.title ?? col.label}
                    sx={{
                      ...headerCellSx,
                      ...(col.key === 'market' ? {} : tightCellSx)
                    }}
                  >
                    {col.sort ? (
                      <TableSortLabel
                        active={sort.column === col.sort}
                        direction={sort.column === col.sort ? sort.direction : 'desc'}
                        onClick={() => toggleSort(col.sort as DescalceSortColumn)}
                      >
                        {col.label}
                      </TableSortLabel>
                    ) : (
                      col.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {viewState !== 'rows' && !everyRowExcluded && !onlyUnresolvedLeft ? (
                <DescalceEmptyState state={viewState} colSpan={COLUMNS.length} />
              ) : onlyUnresolvedLeft ? (
                // 🚨 Nothing was excluded and nothing is filtered: the table is
                // empty because the sweeper REFUSED rules it could not attach
                // to a venue-account pair. The `empty` state would announce
                // "no open positions on any opener rule right now" over rules
                // that hold one — the same claim this screen just took out of
                // the resolved message, and louder.
                <TableRow>
                  <TableCell
                    colSpan={COLUMNS.length}
                    align="center"
                    data-descalce-empty-state="unresolved-empty"
                  >
                    <Typography sx={{ fontSize: '0.82rem', color: 'grey.500', py: 2 }}>
                      No rows to show.{unresolvedSuffix}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : live.length === 0 && excludedUnreadable ? (
                // 🚨 The count came and could not be read, so "empty" and
                // "all excluded" are INDISTINGUISHABLE from here. Picking
                // either one is the same invention the count exists to
                // prevent — in one direction or the other.
                <TableRow>
                  <TableCell
                    colSpan={COLUMNS.length}
                    align="center"
                    data-descalce-empty-state="resolved-empty-unknown"
                  >
                    <Typography sx={{ fontSize: '0.82rem', color: 'grey.500', py: 2 }}>
                      No live rows{resolvedExcluded > 0 ? ` beyond the ${resolvedExcluded} this page excluded` : ''} — and the
                      sweeper&apos;s own count of what it excluded could not be read, so
                      &ldquo;nothing is open&rdquo; and &ldquo;everything resolved&rdquo; cannot be told apart here.
                      {unresolvedSuffix}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : live.length === 0 ? (
                // Every row this sweep PRICED had a leg that already resolved
                // — whether the sweeper left them out (the ordinary path) or
                // sent them and this page dropped them. Deliberately not "every
                // rule holding a position": the rules the sweeper refused for
                // an unresolvable account pair also hold positions and are not
                // here, and they have their own chip. No filter brings any of
                // this back, so "no rows match the current filters" would send
                // the operator clearing filters that were never the reason,
                // and the four backend states would claim nothing is held.
                <TableRow>
                  <TableCell
                    colSpan={COLUMNS.length}
                    align="center"
                    data-descalce-empty-state="resolved-empty"
                  >
                    <Typography sx={{ fontSize: '0.82rem', color: 'grey.500', py: 2 }}>
                      No live rows — every rule this sweep priced had a leg that already resolved at its venue.
                      {unresolvedSuffix}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : visibleRows.length === 0 ? (
                // The snapshot has LIVE rows, but the active client-side
                // filters (search / min descalce / toggles) hide all of them —
                // a distinct message from `DescalceEmptyState`'s four
                // backend-driven states, so "genuinely no positions" and
                // "filtered down to zero" never read the same. The count is of
                // LIVE rows: the excluded ones are not something a filter can
                // give back, and they have their own chip.
                <TableRow>
                  <TableCell
                    colSpan={COLUMNS.length}
                    align="center"
                    data-descalce-empty-state="filtered-empty"
                  >
                    <Typography sx={{ fontSize: '0.82rem', color: 'grey.500', py: 2 }}>
                      No rows match the current filters — {live.length} live row(s) in the snapshot are hidden.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                visibleRows.map(row => (
                  <TableRow key={row.ruleId} hover data-rule-id={row.ruleId}>
                    <TableCell align="right" sx={tightCellSx}>
                      <DescalceCell row={row} />
                    </TableCell>
                    <TableCell sx={tightCellSx}>
                      <SideCell row={row} />
                    </TableCell>
                    <TableCell sx={tightCellSx}>
                      <GroupCell group={row.group} accounts={row.accounts} />
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '0.8rem' }} noWrap title={row.pairName}>
                        {row.pairName}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={tightCellSx}>
                      <VenueCountCell
                        value={legCount(row, 'polymarket', row.venue?.polyYes)}
                      />
                    </TableCell>
                    <TableCell align="right" sx={tightCellSx}>
                      <VenueCountCell
                        value={legCount(row, 'polymarket', row.venue?.polyNo)}
                      />
                    </TableCell>
                    <TableCell align="right" sx={tightCellSx}>
                      <VenueCountCell
                        value={legCount(row, 'kalshi', row.venue?.kalshiYes)}
                      />
                    </TableCell>
                    <TableCell align="right" sx={tightCellSx}>
                      <VenueCountCell
                        value={legCount(row, 'kalshi', row.venue?.kalshiNo)}
                      />
                    </TableCell>
                    <TableCell align="right" sx={tightCellSx}>
                      <BeliefCell row={row} />
                    </TableCell>
                    <TableCell align="right" sx={tightCellSx}>
                      <DiffCell row={row} />
                    </TableCell>
                    <TableCell sx={tightCellSx}>
                      <RuleCell row={row} />
                    </TableCell>
                    <TableCell align="center" sx={tightCellSx}>
                      <TriageCell
                        row={row}
                        counts={triageCountsOf(row)}
                        stale={triage.isStale}
                      />
                    </TableCell>
                    <TableCell align="center" sx={tightCellSx}>
                      <GoCell row={row} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};
